import { createContext, useContext, useEffect, useState, useCallback, useMemo, type ReactNode } from "react";
import { getPublicBranding } from "@/services/settings";
import defaultLogo from "@/assets/mk-logo.png";

export interface BrandingData {
  businessName: string;
  tagline: string;
  logoUrl: string;
}

interface BrandingContextValue {
  businessName: string;
  tagline: string;
  logoUrl: string;
  effectiveLogo: string;
  loading: boolean;
  updateBrandingState: (data: Partial<BrandingData>) => void;
  refreshBranding: () => Promise<void>;
}

const DEFAULT_BRANDING: BrandingData = {
  businessName: "M.K. FINANCIAL SERVICES",
  tagline: "Kameti & Chit Fund Management Workspace",
  logoUrl: "",
};

const BrandingContext = createContext<BrandingContextValue | null>(null);

export function BrandingProvider({ children }: { children: ReactNode }) {
  const [branding, setBranding] = useState<BrandingData>(() => {
    // Attempt to seed from local storage if previously loaded for faster paint
    try {
      const cached = localStorage.getItem("app_branding");
      if (cached) {
        return { ...DEFAULT_BRANDING, ...JSON.parse(cached) };
      }
    } catch {
      // Ignore storage errors
    }
    return DEFAULT_BRANDING;
  });
  const [loading, setLoading] = useState(true);

  const applyDomUpdates = useCallback((data: BrandingData) => {
    // Update document title
    if (data.businessName) {
      document.title = `${data.businessName} | Kameti Management System`;
    }
    // Update favicon
    let link = document.querySelector<HTMLLinkElement>("link[rel*='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = data.logoUrl;
  }, []);

  const refreshBranding = useCallback(async () => {
    try {
      const data = await getPublicBranding();
      const updated: BrandingData = {
        businessName: data.businessName || DEFAULT_BRANDING.businessName,
        tagline: data.tagline || DEFAULT_BRANDING.tagline,
        logoUrl: data.logoUrl || "",
      };
      setBranding(updated);
      try {
        localStorage.setItem("app_branding", JSON.stringify(updated));
      } catch {
        // Ignore
      }
      applyDomUpdates(updated);
    } catch {
      // Keep existing/cached branding on failure
    } finally {
      setLoading(false);
    }
  }, [applyDomUpdates]);

  const updateBrandingState = useCallback((patch: Partial<BrandingData>) => {
    setBranding((prev) => {
      const next = { ...prev, ...patch };
      try {
        localStorage.setItem("app_branding", JSON.stringify(next));
      } catch {
        // Ignore
      }
      applyDomUpdates(next);
      return next;
    });
  }, [applyDomUpdates]);

  useEffect(() => {
    refreshBranding();
  }, [refreshBranding]);

  const effectiveLogo = branding.logoUrl || defaultLogo;

  const value = useMemo<BrandingContextValue>(
    () => ({
      businessName: branding.businessName,
      tagline: branding.tagline,
      logoUrl: branding.logoUrl,
      effectiveLogo,
      loading,
      updateBrandingState,
      refreshBranding,
    }),
    [branding, effectiveLogo, loading, updateBrandingState, refreshBranding]
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding(): BrandingContextValue {
  const ctx = useContext(BrandingContext);
  if (!ctx) {
    throw new Error("useBranding must be used within BrandingProvider");
  }
  return ctx;
}
